import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import api from '../api';

export default function CheckEmail() {
  const location = useLocation();
  const email = location.state?.email || '';
  const [resendState, setResendState] = useState('idle'); // 'idle' | 'sending' | 'sent' | 'error' | 'rate_limited'
  const [retryAfter, setRetryAfter] = useState(0);

  async function handleResend() {
    if (!email || resendState === 'sending') return;
    setResendState('sending');
    try {
      const { data } = await api.post('/auth/resend-verification', { email });
      if (data.alreadyVerified) {
        setResendState('sent'); // edge case — they're verified, just go log in
      } else {
        setResendState('sent');
      }
      // Reset after 60s so they can resend again
      setTimeout(() => setResendState('idle'), 60000);
    } catch (err) {
      if (err.response?.status === 429) {
        setRetryAfter(err.response.data?.retryAfter || 60);
        setResendState('rate_limited');
        setTimeout(() => setResendState('idle'), (err.response.data?.retryAfter || 60) * 1000);
      } else {
        setResendState('error');
        setTimeout(() => setResendState('idle'), 4000);
      }
    }
  }

  const containerStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1a2535 0%, #1a3a5c 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
  };
  const cardStyle = {
    background: '#fff', borderRadius: 16, padding: 36, width: '100%', maxWidth: 440,
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  };

  return (
    <div style={containerStyle}>
      <div style={{ width: '100%', maxWidth: 440 }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <img src="/jobhub-logo-dark.png" alt="JobHub" style={{ height: 100, width: 'auto', objectFit: 'contain' }} />
        </div>

        <div style={cardStyle}>
          {/* Icon */}
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{ width: 72, height: 72, background: '#eff6ff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36, margin: '0 auto 16px' }}>
              ✉️
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: '0 0 8px' }}>Check your inbox</h2>
            <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6, margin: 0 }}>
              We've sent a verification link to{' '}
              {email ? <strong style={{ color: '#111827' }}>{email}</strong> : 'your email address'}.
              Click the link to activate your account.
            </p>
          </div>

          {/* Steps */}
          <div style={{ background: '#f9fafb', borderRadius: 10, padding: 16, marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 10 }}>Next steps</div>
            {[
              { icon: '📬', text: 'Open the email from Jobhub' },
              { icon: '🔗', text: 'Click "Verify my email address"' },
              { icon: '🚀', text: "You'll be logged in automatically" },
            ].map(({ icon, text }) => (
              <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, fontSize: 13, color: '#374151' }}>
                <span style={{ fontSize: 16, width: 22, textAlign: 'center' }}>{icon}</span>
                {text}
              </div>
            ))}
          </div>

          {/* Expiry notice */}
          <div style={{ background: '#fef9c3', border: '1px solid #fde047', borderRadius: 8, padding: '10px 14px', marginBottom: 20, fontSize: 12, color: '#854d0e' }}>
            ⏱ The link expires in <strong>24 hours</strong>.
          </div>

          {/* Resend section */}
          <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: 18, textAlign: 'center' }}>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>Didn't receive the email?</p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, color: '#6b7280', textAlign: 'left', background: '#f9fafb', padding: '10px 14px', borderRadius: 8, marginBottom: 14 }}>
              <div>• Check your <strong>spam or junk folder</strong></div>
              <div>• Make sure <strong>{email || 'your email'}</strong> is correct</div>
              <div>• Allow a minute for delivery</div>
            </div>

            <button
              onClick={handleResend}
              disabled={resendState === 'sending' || resendState === 'sent' || resendState === 'rate_limited'}
              style={{
                width: '100%', padding: '11px', border: '1px solid #d0d7de', borderRadius: 8,
                background: resendState === 'sent' ? '#f0fdf4' : '#fff',
                color: resendState === 'sent' ? '#16a34a' : '#374151',
                fontSize: 14, fontWeight: 600, cursor: (resendState !== 'idle') ? 'not-allowed' : 'pointer',
                marginBottom: 12, transition: 'all 0.15s',
              }}
            >
              {resendState === 'idle' && '↩ Resend verification email'}
              {resendState === 'sending' && '⏳ Sending...'}
              {resendState === 'sent' && '✅ Email sent! Check your inbox'}
              {resendState === 'rate_limited' && `⏱ Wait ${retryAfter}s before resending`}
              {resendState === 'error' && '❌ Failed to send — try again'}
            </button>

            <Link
              to="/login"
              style={{ display: 'block', fontSize: 13, color: '#6b7280', textDecoration: 'none', textAlign: 'center' }}
            >
              ← Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
